import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import api from "../utils/api";
import { apiUrls } from "../utils/urls";
import { useProfile } from "./ProfileContext";
import { groups } from "../constants/auth";

const ImpersonationContext = createContext();

export const useImpersonation = () => useContext(ImpersonationContext);

const IMPERSONATION_CONFIG = {
  parent: {
    storageKey: "impersonatedParent",
    unknownLabel: "Unknown Parent",
  },
  student: {
    storageKey: "impersonatedStudent",
    unknownLabel: "Unknown Student",
  },
};

function getUserType(userType) {
  return userType === "student" ? "student" : "parent";
}

function buildStudentLabel(student) {
  const firstName = student.first_name || student.user?.first_name || "";
  const lastName = student.last_name || student.user?.last_name || "";
  const fallbackLabel = `${firstName} ${lastName}`.trim();
  return fallbackLabel || student.email || "Unknown Student";
}

function isImpersonateRoute() {
  if (typeof window === "undefined") {
    return false;
  }
  return window.location.pathname.startsWith("/impersonate/");
}

export const ImpersonationProvider = ({ children, userType = "parent" }) => {
  const { profile, isLoaded: isProfileLoaded } = useProfile();
  const resolvedUserType = getUserType(userType);
  const config = IMPERSONATION_CONFIG[resolvedUserType];
  const isEmployeeUser = Boolean(
    isProfileLoaded && profile?.groups?.includes(groups.EMPLOYEES)
  );
  const isParentUser = Boolean(
    isProfileLoaded &&
      profile?.groups?.includes(groups.PARENTS) &&
      !isEmployeeUser
  );
  const storageKeys = useMemo(
    () => [
      config.storageKey,
      ...Object.values(IMPERSONATION_CONFIG)
        .map((item) => item.storageKey)
        .filter((key) => key !== config.storageKey),
    ],
    [config.storageKey]
  );
  const [impersonatedProfile, setImpersonatedProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  // Bumped whenever impersonation is explicitly set/cleared, or a new restore starts.
  // In-flight restores must check this so they cannot overwrite a newer target.
  const refreshGenerationRef = useRef(0);

  const normalizeImpersonation = useCallback(
    (profile) => {
      if (!profile || typeof profile !== "object") {
        return null;
      }

      const data = profile.data || {};
      const firstName =
        data.user?.first_name || data.user__first_name || data.first_name || "";
      const lastName =
        data.user?.last_name || data.user__last_name || data.last_name || "";
      const email = data.user?.email || data.user__email || data.email || "";
      const fallbackLabel = `${firstName} ${lastName}`.trim();
      const userProfileId =
        data.user_profile_id ||
        data.user?.profile?.uuid ||
        data.user__profile__uuid ||
        null;

      return {
        uuid: profile.uuid || data.uuid || null,
        label: profile.label || fallbackLabel || email || config.unknownLabel,
        data: {
          ...data,
          user_profile_id: userProfileId,
          user: {
            ...(data.user || {}),
            first_name: firstName,
            last_name: lastName,
            email,
          },
        },
      };
    },
    [config.unknownLabel]
  );

  const fetchParentStudentProfile = useCallback(
    async (profileUuid, parentProfileUuid) => {
      try {
        const response = await api.get(apiUrls.parent(parentProfileUuid).students());
        const student = (response.data?.data?.students || []).find(
          (row) => row.uuid === profileUuid
        );
        if (!student) {
          return null;
        }

        return normalizeImpersonation({
          uuid: student.uuid,
          label: buildStudentLabel(student),
          data: {
            ...student,
            user: {
              first_name: student.first_name,
              last_name: student.last_name,
              email: student.email,
            },
          },
        });
      } catch (error) {
        console.error("Failed to fetch parent student impersonation data:", error);
        return null;
      }
    },
    [normalizeImpersonation]
  );

  const fetchImpersonatedProfile = useCallback(
    async (profileUuid) => {
      if (!isProfileLoaded) {
        return null;
      }

      const parentProfileUuid = profile?.parent?.uuid;
      if (isParentUser) {
        if (!parentProfileUuid) {
          return null;
        }
        return fetchParentStudentProfile(profileUuid, parentProfileUuid);
      }

      if (!isEmployeeUser) {
        return null;
      }

      try {
        const response = await api.get(apiUrls.admin.impersonationOptions(), {
          params: {
            user_type: resolvedUserType,
            profile_uuid: profileUuid,
          },
        });

        const match = response.data?.data?.[0];
        if (match) {
          return normalizeImpersonation(match);
        }
        return null;
      } catch (error) {
        console.error("Failed to fetch impersonation profile data:", error);
        return null;
      }
    },
    [
      fetchParentStudentProfile,
      isEmployeeUser,
      isParentUser,
      isProfileLoaded,
      normalizeImpersonation,
      profile?.parent?.uuid,
      resolvedUserType,
    ]
  );

  useEffect(() => {
    const loadAndRefresh = async () => {
      // Impersonate pages own the handoff for this load. Restoring the previous
      // localStorage target here races with setImpersonation(newTarget).
      if (isImpersonateRoute()) {
        setIsLoading(false);
        return;
      }

      const generation = ++refreshGenerationRef.current;
      let hasStoredImpersonation = false;
      try {
        const stored =
          storageKeys
            .map((key) => localStorage.getItem(key))
            .find((value) => !!value) || null;
        if (stored) {
          hasStoredImpersonation = true;
          const parsed = normalizeImpersonation(JSON.parse(stored));
          if (generation !== refreshGenerationRef.current) {
            return;
          }
          setImpersonatedProfile(parsed);

          if (!isProfileLoaded) {
            return;
          }

          setIsRefetching(true);
          const targetUuid = parsed?.uuid;
          const freshData = targetUuid
            ? await fetchImpersonatedProfile(targetUuid)
            : null;

          if (generation !== refreshGenerationRef.current) {
            return;
          }

          if (freshData) {
            setImpersonatedProfile((current) => {
              // A newer setImpersonation/clear won while this refresh was in flight.
              if (current?.uuid && current.uuid !== targetUuid) {
                return current;
              }
              return freshData;
            });
          }
          setIsRefetching(false);
        }
      } catch (error) {
        console.error("Failed to load impersonation state:", error);
        if (generation === refreshGenerationRef.current) {
          setIsRefetching(false);
        }
      } finally {
        if (
          generation === refreshGenerationRef.current &&
          (!hasStoredImpersonation || isProfileLoaded)
        ) {
          setIsLoading(false);
        }
      }
    };

    loadAndRefresh();
  }, [
    fetchImpersonatedProfile,
    isProfileLoaded,
    normalizeImpersonation,
    storageKeys,
  ]);

  useEffect(() => {
    if (!isLoading) {
      if (impersonatedProfile) {
        localStorage.setItem(config.storageKey, JSON.stringify(impersonatedProfile));
      } else {
        localStorage.removeItem(config.storageKey);
      }
      storageKeys
        .filter((key) => key !== config.storageKey)
        .forEach((key) => localStorage.removeItem(key));
    }
  }, [config.storageKey, impersonatedProfile, isLoading, storageKeys]);

  const setImpersonation = useCallback(
    (profile) => {
      refreshGenerationRef.current += 1;
      setIsRefetching(false);
      setImpersonatedProfile(normalizeImpersonation(profile));
      setIsLoading(false);
    },
    [normalizeImpersonation]
  );

  const clearImpersonation = useCallback(() => {
    refreshGenerationRef.current += 1;
    setIsRefetching(false);
    setImpersonatedProfile(null);
  }, []);

  const isReadOnlyImpersonation =
    Boolean(impersonatedProfile) &&
    !profile?.groups?.includes(groups.EMPLOYEES);

  return (
    <ImpersonationContext.Provider
      value={{
        userType: resolvedUserType,
        impersonatedProfile,
        // Backward compatibility aliases.
        impersonatedParent: impersonatedProfile,
        impersonatedStudent: impersonatedProfile,
        setImpersonation,
        clearImpersonation,
        isImpersonating: !!impersonatedProfile,
        isReadOnlyImpersonation,
        isLoading,
        isRefetching,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
};
